import React, { useState, useMemo, useEffect } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Badge,
} from "reactstrap"
import { FaSearch, FaEye, FaFileInvoice } from "react-icons/fa"
import { FaChevronCircleLeft, FaChevronCircleRight } from "react-icons/fa"
import Swal from "sweetalert2"
import moment from "moment"

import OrderModal from "./OrderModal" // ← View modal (create below)
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import PartialModal from "./PartialModal"
import InvoiceModal from "./invoiceModal"

const Order = () => {
  const [orders, setOrders] = useState([])
  const [products, setProducts] = useState([])
  const [orderData, setOrderData] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState("") // only "view" for now

  const [searchString, setSearchString] = useState("")
  const [perPageItem, setPerPageItem] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [invoiceModal, setInvoiceModal] = useState(false)

  const [loading, setLoading] = useState(false)

  const getAllOrders = async () => {
    setLoading(true)
    try {
      const response = await fetchData(setLoading, "order")
      if (response?.success) {
        setOrders(response.data.reverse() || [])
      } else {
        Swal.fire(
          "Error",
          response?.message || "Failed to load orders",
          "error"
        )
      }
    } catch (err) {
      console.error(err)
      Swal.fire("Error", "Network or server error", "error")
    } finally {
      setLoading(false)
    }
  }

  const getAllProducts = async () => {
    try {
      const response = await fetchData(() => {}, "product/catalog")
      if (response?.success) {
        setProducts(response.data || [])
      }
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    getAllOrders()
    getAllProducts()
  }, [])

  const inactiveProductIds = useMemo(() => {
    return new Set(
      products.filter(p => !p.status).map(p => p.id)
    )
  }, [products])

  const filteredOrders = useMemo(() => {
    let result = orders

    // Exclude orders containing inactive-stock products
    if (inactiveProductIds.size > 0) {
      result = result.filter(order => {
        const productIds = order.productIdsAndQty?.map(p => p.id) || []
        return !productIds.some(id => inactiveProductIds.has(id))
      })
    }

    if (!searchString.trim()) return result

    const lowerSearch = searchString.toLowerCase()

    return result.filter(order => {
      const productsStr =
        order.productIdsAndQty
          ?.map(p => `${p.productName}`)
          .join(" ")
          .toLowerCase() || ""

      return (
        order.orderId?.toLowerCase().includes(lowerSearch) ||
        order.status?.toLowerCase().includes(lowerSearch) ||
        productsStr.includes(lowerSearch)
      )
    })
  }, [orders, searchString, inactiveProductIds])

  const totalItems = filteredOrders.length
  const totalPages = Math.ceil(totalItems / perPageItem)

  const indexOfLastItem = currentPage * perPageItem
  const indexOfFirstItem = indexOfLastItem - perPageItem
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    setCurrentPage(1)
  }, [searchString, perPageItem])

  const openViewModal = order => {
    setModalType("view")
    setOrderData(order)
    setIsModalOpen(true)
  }

  const openInvoice = order => {
    setOrderData(order)
    setInvoiceModal(true)
  }

  const [partialModal, setPartialModal] = useState(false)

  const STATUS_FLOW = {
    pending: ["Approved", "Rejected", "approvepartially"],
    approved: ["Delivered"],
    approvepartially: ["Delivered"],
    delivered: [],
    rejected: [],
  }

  const handleStatusChange = (order, newStatusRaw) => {
    const newStatus = newStatusRaw.trim().toLowerCase()
    const currentStatus = order.status?.trim().toLowerCase()

    // Same status selected → do nothing
    if (currentStatus === newStatus) return

    const allowedNext =
      STATUS_FLOW[currentStatus]?.map(s => s.toLowerCase()) || []

    // Partial approval → open modal immediately if allowed
    if (
      newStatus === "approvepartially" &&
      allowedNext.includes("approvepartially")
    ) {
      setOrderData(order)
      setPartialModal(true)
      return
    }

    // Invalid transition
    if (!allowedNext.includes(newStatus)) {
      Swal.fire({
        icon: "warning",
        title: "Action Not Allowed",
        html: `
        <strong>${capitalize(
          currentStatus
        )}</strong> order cannot be changed to 
        <strong>${capitalize(newStatus)}</strong>.
      `,
        confirmButtonText: "OK",
      })
      return
    }

    // Valid direct update
    updateOrderStatus(order.id, newStatus)
  }

  // Utility to capitalize first letter
  const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)

  const updateOrderStatus = async (orderId, status) => {
    console.log(orderId)
    const res = await FetchPutData(setLoading, `order`, orderId, {
      status,
    })

    if (res?.data?.success) {
      Swal.fire("Success", "Order status updated", "success")
      getAllOrders()
    } else {
      Swal.fire("Error", res?.response?.data?.message || "Failed", "error")
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        {loading && <Loader />}

        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Orders"
          description="View and manage all orders"
        />

        <Row>
          <Col lg="12" className="px-0">
            <Card className="mb-0 shadow-lg">
              <CardBody>
                {/* Controls */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="position-relative">
                      <input
                        className="form-control rounded-4 ps-3 pe-5"
                        placeholder="Search by Order ID, status or product..."
                        value={searchString}
                        onChange={e => setSearchString(e.target.value)}
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                    </div>

                    <select
                      className="form-select rounded-4 w-auto"
                      value={perPageItem}
                      onChange={e => setPerPageItem(Number(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                {/* Orders Table */}
                <Table responsive hover className="align-middle">
                  <thead className="bg-blue text-white">
                    <tr>
                      <th>#</th>
                      <th>Order ID</th>
                      <th>Placed By</th>
                      <th>Location</th>
                      <th>Expected Delivery</th>
                      <th style={{ width: "15%" }}>Chargable Products</th>
                      <th style={{ width: "15%" }}>Non-Chargable Products</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th className="text-center">Actions</th>
                      <th className="text-center">Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-4">
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      currentOrders.map((order, idx) => (
                        <tr key={order.id}>
                          <td>{indexOfFirstItem + idx + 1}</td>
                          <td>
                            <strong>{order.orderId}</strong>
                          </td>

                          <td>{order.placedBy?.name || "—"}</td>
                          <td>{order.location?.name || "—"}</td>
                          <td>
                            {order.employeeExpectedDeliverDate
                              ? moment(
                                  order.employeeExpectedDeliverDate
                                ).format("DD/MM/YYYY")
                              : "—"}
                          </td>
                          <td>
                            {order?.productIdsAndQty
                              ?.filter(item => item?.isChargeable)
                              ?.map(item => item?.productName)
                              ?.join(" , ")}
                          </td>
                          <td>
                            {order?.productIdsAndQty
                              ?.filter(item => !item?.isChargeable)
                              ?.map(item => item?.productName)
                              ?.join(" , ")}
                          </td>
                          <td>
                            <select
                              className="form-select pe-2"
                              style={{ minWidth: "160px" }}
                              value={order.status?.toLowerCase().replace(/\s+/g, "")}
                              onChange={e =>
                                handleStatusChange(order, e.target.value)
                              }
                            >
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                              <option value="delivered">Delivered</option>
                              <option value="approvepartially">
                                Approve partially
                              </option>
                            </select>
                          </td>
                          <td>
                            {order.createdAt
                              ? moment(order.createdAt).format("DD/MM/YYYY")
                              : "—"}
                          </td>
                          <td className="text-center">
                            <Button
                              className="btn-blue"
                              onClick={() => openViewModal(order)}
                            >
                              <FaEye />
                            </Button>
                          </td>
                          <td className="text-center">
                            <Button
                              className="btn-blue"
                              onClick={() => openInvoice(order)}
                            >
                              <FaFileInvoice />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* Pagination */}
                {totalItems > 0 && (
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                    <div className="text-muted">
                      Showing {currentOrders.length} of {totalItems} orders
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <Button
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        <FaChevronCircleLeft />
                      </Button>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <Button
                          key={i}
                          size="sm"
                          color={currentPage === i + 1 ? "primary" : "light"}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </Button>
                      ))}

                      <Button
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        <FaChevronCircleRight />
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <OrderModal
        isOpen={isModalOpen}
        toggle={() => setIsModalOpen(false)}
        orderData={orderData}
        modalType={modalType}
      />

      <PartialModal
        isOpen={partialModal}
        toggle={() => setPartialModal(false)}
        orderData={orderData}
        refreshOrders={getAllOrders}
      />

      <InvoiceModal
        isOpen={invoiceModal}
        toggle={() => setInvoiceModal(false)}
        orderData={orderData}
      />
    </div>
  )
}

export default Order
